import { DownOutlined } from '@ant-design/icons';
import { useBoolean, useLocalStorageState, useSetState } from 'ahooks';
import { Card, CardProps, Radio, Select, SelectProps, TabPaneProps, Tabs, Tooltip } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import classnames from 'classnames';
import { isEmpty, flattenDeep } from 'lodash';
import moment from 'moment/moment';
import React, { CSSProperties, FC, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadLine from '@/assets/images/icons/download-line.react.svg?react';
import { exportCSV } from '@/infrastructure/export-csv';
import { getProductId, isAndroid, isAppleTV, isIOS } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { DATE_TIME_FORMAT, formatTimestamp, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import styles from '@/presentation/components/per-issue-trend-analysis/index.module.scss';
import { Chart } from '@/presentation/components/per-issue-trend-analysis/line-chart';
import { getIssueTrend } from '@/presentation/components/problem-detail-analysis/api';
import { ISSUE_TREND_ANALYSIS_VISIBLE } from '@/presentation/config/storage';
import { PropsWithStyles } from '@/proto/common';
import { getIssueTypeMapLabel } from '@/proto/field';
import { enumTrpc } from '@/proto/generated/tpro_rmonitor_web_server_issue_statistics';
import { IGetTrendReq, ITrend, StatData } from '@/proto/statistics';
import { MatchMode, NUMBER_MATCH_OPTIONS } from '../form/standards/types';

type TimeType = enumTrpc.rmonitor.web_server.TimeType;

const { TimeType } = enumTrpc.rmonitor.web_server;
const { IssueMetricType } = enumTrpc.rmonitor.web_server;

type MetricGroupItem = {
  stat: StatData;
  name: string;
};
type CombineDisplayItem = {
  name: TabPaneProps['tab'];
  tabKey: any;
  unitConfig?: { // 单位配置 不传则无单位切换
    defaultUnit: SelectProps['value']; // 默认值
    options: SelectProps['options']; // 单位选项
    unitFormat: (value: SelectProps['value']) => (value: (undefined | number | null)) => (undefined | number | null); // 单位格式化方法
  },
};

export interface ItabConfigs {
  // common包里里面的枚举每次更新，依赖的协议全部要更新一遍，这里原来依赖的某一个协议包打进去的，没法及时更新
  // 需要找时间统一把IssueType这些导出统一成common包里面的
  key: any; // tab key
  name: TabPaneProps['tab']; // tab名称
  isTotalSize?: boolean; // 是否支持累计
  unitConfig?: { // 单位配置 不传则无单位切换
    defaultUnit: SelectProps['value']; // 默认值
    options: SelectProps['options']; // 单位选项
    unitFormat: (value: SelectProps['value']) => (value: (undefined | number | null)) => (undefined | number | null); // 单位格式化方法
  },
  stringName?: string; // string名称
  hidden?: boolean;
  // 用于一个tab下展示不同分位值趋势
  metricStateGroup?: MetricGroupItem[];
  // 用于一个tab下双y轴展示两组数据
  combineDisplayGroup?: CombineDisplayItem[]
}

interface IProps {
  pgId: string;
  issueType: any;
  token: string;
  headToken: string;
  issueId?: string;
  timeWindow?: TimeType;
  defaultTotalSize?: boolean;
  defaultTab?: any;
  tabConfigs: ItabConfigs[];
  searchParams?: Record<string, any>;
  queryCompareToken?: (params: Record<string, any>) => Promise<string>;
  tabBarStyle?: CSSProperties;
  cardProps?: CardProps;

  // 自定义请求数据，要求返回数据格式为XXX
  request?: (
    token: string,
    metricType: any,
    timeWindow?: number,
    statisticalDimension?: boolean,
    stat?: StatData,
  ) => Promise<ITrend[]>;

  uuidFetch?: string;
  uuidControl?: boolean;
  showDownload?: boolean; // 是否显示导出
  showSwitch?: boolean;
  showTitle?: boolean;
}
export interface IState {
  unit?: SelectProps['value'];
  totalSize?: boolean,
  tabKey: any;
}

const { TabPane } = Tabs;

export const PerIssueTrendAnalysis: FC<PropsWithStyles<IProps>> = forwardRef((props: IProps, ref) => {
  const { t } = useTranslation();
  const {
    timeWindow,
    pgId,
    tabConfigs,
    defaultTotalSize,
    defaultTab,
    issueType,
    token,
    issueId,
    searchParams,
    queryCompareToken,
    showDownload = true,
    showSwitch = true,
    showTitle = true,
    headToken,
  } = props;

  // 获取趋势分析数据
  const defaultFetcher = async (
    token: string,
    metricType: any,
    timeWindow?: number,
    statisticalDimension?: boolean,
    stat?: StatData,
  ) => {
    const params: IGetTrendReq = {
      issueType,
      productId: getProductId(),
      metricType,
      totalize: statisticalDimension,
      timeWindow,
      issueId: issueId || undefined,
      token,
      Stat: stat,
    };
    return await getIssueTrend(params);
  };

  const getChartData = props.request || defaultFetcher;
  const [storageVisible, setStorageVisible] = useLocalStorageState<boolean>(
    `${ISSUE_TREND_ANALYSIS_VISIBLE}-${pgId}`,
    {
      defaultValue: true,
    },
  );
  const [groupData, setGroupData] = useState<Record<React.Key, ITrend[]>>({});
  const [chartData, setChartData] = useState<ITrend[]>([]);
  const [compareChartData, setCompareChartData] = useState<ITrend[] | undefined>(undefined);
  const [loadingState, setLoadingState] = useBoolean(false);
  const [state, setState] = useSetState<IState>({
    unit: tabConfigs.find(config => config.key === defaultTab)?.unitConfig?.defaultUnit
      ?? tabConfigs[0]?.combineDisplayGroup?.find(group => group?.unitConfig && ('options' in group?.unitConfig))?.unitConfig?.defaultUnit
      ?? tabConfigs[0].unitConfig?.options?.[0].value,
    totalSize: defaultTotalSize || false,
    tabKey: defaultTab ?? tabConfigs[0].key,
  });
  const [lagRateTips, setLagRateTips] = useState('');

  useImperativeHandle(ref, () => ({ setLoadingState }));

  const unitChange = (value: string) => {
    setState({ unit: value });
  };

  const totalSizeChange = (e: RadioChangeEvent) => {
    setState({ totalSize: e.target.value });
  };

  useEffect(() => {
    if (headToken && timeWindow) {
      load();
    }
  }, [headToken, state.tabKey, state.totalSize, props.timeWindow]);

  useEffect(() => {
    if (state.tabKey !== IssueMetricType.ISSUE_METRIC_LAG_REPORT_RATE) {
      return;
    }
    if (
      state.tabKey === IssueMetricType.ISSUE_METRIC_LAG_REPORT_RATE
      && searchParams?.standardTimeCost?.value && searchParams?.standardTimeCost?.value?.length !== 0
    ) {
      const operation = NUMBER_MATCH_OPTIONS.find(option => option.value === searchParams?.standardTimeCost?.operator)
        ?.label;
      setLagRateTips(searchParams?.standardTimeCost?.operator !== MatchMode.Between
        ? t('当前展示的是卡顿耗时{{operation}}{{value}}ms时的卡顿率。', { operation, value: searchParams?.standardTimeCost?.value?.[0] })
        : t('当前展示的是卡顿耗时位于[{{start}}ms,{{end}}ms]{{operation}}时的卡顿率。', { start: searchParams?.standardTimeCost?.value?.[0], end: searchParams?.standardTimeCost?.value?.[1], operation }));
    } else if (
      state.tabKey === IssueMetricType.ISSUE_METRIC_LAG_REPORT_RATE
    ) {
      setLagRateTips(t('目前未对卡顿耗时进行限制。您可以通过调整卡顿耗时筛选条件，分析不同耗时区间下的卡顿率表现。'));
    }
  }, [state.tabKey, searchParams]);

  // uuid控制
  useEffect(() => {
    if (props.uuidControl && props.uuidFetch && token && headToken) {
      load();
    }
  }, [props.uuidFetch]);

  const load = async () => {
    setLoadingState.setTrue();

    if (tabConfig?.metricStateGroup && !isEmpty(tabConfig.metricStateGroup)) {
      const groupDataObj: Record<string, ITrend[]> = {};
      const groupData = await Promise.all(tabConfig.metricStateGroup
        .map(async config => await getChartData(token, state.tabKey, timeWindow, state.totalSize, config.stat)));

      tabConfig.metricStateGroup.forEach((config, index) => {
        groupDataObj[config.stat] = groupData[index];
      });

      setGroupData(groupDataObj);
    } else if (tabConfig?.combineDisplayGroup && !isEmpty(tabConfig.combineDisplayGroup)) {
      const groupDataObj: Record<string, ITrend[]> = {};
      const groupData = await Promise.all(tabConfig.combineDisplayGroup
        .map(async config => await getChartData(token, config.tabKey, timeWindow, state.totalSize)));

      tabConfig.combineDisplayGroup.forEach((config, index) => {
        groupDataObj[config.tabKey] = groupData[index];
      });

      setGroupData(groupDataObj);
    } else {
      // 主数据的统计维度逻辑：只有在主时间窗口是5分钟或1小时时才使用用户选择的统计维度，否则强制为非累计
      const mainTotalize = timeWindow
        && [TimeType.TIME_TYPE_MINUTE5, TimeType.TIME_TYPE_HOUR].includes(timeWindow)
        ? state.totalSize
        : false;
      // 对比数据的统计维度逻辑：只有在对比时间窗口是5分钟或1小时时才使用用户选择的统计维度，否则强制为非累计
      const compareTotalize = searchParams?.timeWindowCompare
        && [TimeType.TIME_TYPE_MINUTE5, TimeType.TIME_TYPE_HOUR].includes(searchParams.timeWindowCompare)
        ? state.totalSize
        : false;


      const totalize = mainTotalize && compareTotalize;
      const data = await getChartData(
        token, state.tabKey, timeWindow,
        searchParams?.isCompareOpen
          ? totalize
          : state.totalSize,
      );
      if (searchParams?.isCompareOpen) {
        const values: Record<string, any> = { ...searchParams };

        Object.keys(searchParams).filter(item => (item.includes('Compare') || item.includes('compare')))
          .forEach((item) => {
            values[item.replace('Compare', '').replace('_compare', '')] = searchParams[item];
          });
        if (values.customDate) {
          const dateRange = getDateRangeByShortCut(values.customDate);
          values.startDateStr = dateRange[0].format(DATE_TIME_FORMAT);
          values.endDateStr = dateRange[1].format(DATE_TIME_FORMAT);
        } else {
          values.startDateStr = moment(values.timeRange[0])
            .format(DATE_TIME_FORMAT);
          values.endDateStr = moment(values.timeRange[1])
            .format(DATE_TIME_FORMAT);
        }

        const compareToken = await queryCompareToken?.(values) || '';
        const data = await getChartData(compareToken, state.tabKey, searchParams?.timeWindowCompare, totalize);
        setCompareChartData(data);
      } else {
        setCompareChartData(undefined);
      }

      setChartData(data);
    }
    setLoadingState.setFalse();
  };

  const tabChange = (value: string) => {
    setState(_state => ({
      tabKey: Number(value),
      unit: tabConfigs.find(config => config.key === Number(value))?.unitConfig?.defaultUnit
        ?? tabConfigs.find(config => config.key === Number(value))?.combineDisplayGroup?.find(group => group?.unitConfig && ('options' in group?.unitConfig))?.unitConfig?.defaultUnit
        ?? tabConfigs[0].unitConfig?.options?.[0].value
    }));
  };


  // 导出数据
  const exportData = async () => {
    const conditionA = searchParams?.isCompareOpen ? t('(条件A)') : '';
    const timePoints = chartData.filter(item => item.time < moment().unix())
      .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT));
    const head = [[
      t('时间范围{{conditionA}}', { conditionA }),
      ...flattenDeep(tabConfigs.map((item) => {
        if (item.metricStateGroup) {
          return item.metricStateGroup.map(metric => t('{{name}}-{{metric}}{{unit}}{{conditionA}}', { name: item?.stringName || item.name, metric: metric.name, unit: item.unitConfig ? `（${item.unitConfig.defaultUnit}）` : '', conditionA }));
        }
        if (item.combineDisplayGroup) {
          return item.combineDisplayGroup.map(metric => t('{{name}}{{conditionA}}', { name: metric.name, conditionA }));
        }
        return t('{{name}}{{unit}}{{conditionA}}', { name: item?.stringName || item.name, unit: item.unitConfig ? `（${item.unitConfig.defaultUnit}）` : '', conditionA });
      })),
    ]];

    const allData = await Promise.all(flattenDeep(tabConfigs.map((config) => {
      if (config.metricStateGroup) {
        return config.metricStateGroup.map(metricState => getChartData(
          token,
          config.key,
          timeWindow,
          config.isTotalSize ? state.totalSize : false,
          metricState.stat,
        ));
      }
      if (config.combineDisplayGroup) {
        return config.combineDisplayGroup.map(group => getChartData(
          token,
          group.tabKey,
          timeWindow,
          config.isTotalSize ? state.totalSize : false,
        ));
      }
      return getChartData(
        token,
        config.key,
        timeWindow,
        config.isTotalSize ? state.totalSize : false,
      );
    })));
    const metricTimePoints = allData[0]
      ?.filter(item => item.time < moment().unix())
      ?.map(item => formatTimestamp(item.time, DATE_TIME_FORMAT)) || [];
    const body = [];
    for (let i = 0; i < (timePoints.length || metricTimePoints.length); i++) {
      const row = [];
      for (const data of allData) {
        row.push(data[i].value);
      }
      body.push([
        timePoints[i] || metricTimePoints[i],
        ...row,
      ]);
    }
    if (searchParams?.isCompareOpen) {
      head[0].push(
        t('时间范围(条件B)'),
        ...tabConfigs.map(item => t('{{name}}{{unit}}(条件B)', { name: item?.stringName || item.name, unit: item.unitConfig ? `（${item.unitConfig.defaultUnit}）` : '' })),
      );
      const values: Record<string, any> = { ...searchParams };
      Object.keys(searchParams).filter(item => (item.includes('Compare') || item.includes('compare')))
        .forEach((item) => {
          values[item.replace('Compare', '').replace('_compare', '')] = searchParams[item];
        });
      if (values.customDate) {
        const dateRange = getDateRangeByShortCut(values.customDate);
        values.startDateStr = dateRange[0].format(DATE_TIME_FORMAT);
        values.endDateStr = dateRange[1].format(DATE_TIME_FORMAT);
      } else {
        values.startDateStr = moment(values.timeRange[0])
          .format(DATE_TIME_FORMAT);
        values.endDateStr = moment(values.timeRange[1])
          .format(DATE_TIME_FORMAT);
      }
      const compareTimePoints = compareChartData?.filter?.(item => item.time < moment().unix())
        .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT)) || [];
      const compareToken = await queryCompareToken?.(values) || '';

      const compareAllData = await Promise.all(tabConfigs.map(config => getChartData(
        compareToken, config.key, timeWindow,
        config.isTotalSize ? state.totalSize : false,
      )));

      for (let i = 0; i < compareTimePoints.length; i++) {
        const row = [];
        for (const data of compareAllData) {
          row.push(data[i].value);
        }
        if (body?.[i]) {
          body?.[i].push(
            compareTimePoints[i],
            ...row,
          );
        } else {
          body.push([
            '',
            ...row.map(() => ''),
            compareTimePoints[i],
            ...row,
          ]);
        }
      }
    }
    await exportCSV(t('{{label}}-趋势分析', { label: getIssueTypeMapLabel(issueType) }), head, body);
  };


  const tabConfig = useMemo(() => tabConfigs.find(config => config.key === state.tabKey), [state.tabKey, tabConfigs]);
  const unitOptions = useMemo(
    () => tabConfig?.unitConfig?.options,
    [state.tabKey, tabConfig],
  );
  const unitOptionsForCombine = useMemo(
    () => tabConfig?.combineDisplayGroup?.find(group => group?.unitConfig && ('options' in group?.unitConfig))?.unitConfig?.options,
    [state.tabKey, tabConfig],
  );

  const unitLabel = useMemo(
    () => String(tabConfig?.unitConfig?.options?.find?.(item => item.value === state.unit)?.value || ''),
    [tabConfig, state.unit, state.tabKey],
  );

  const showStatisticalDimension = () => {
    if (!timeWindow) {
      return false;
    }

    if (![TimeType.TIME_TYPE_MINUTE5, TimeType.TIME_TYPE_HOUR].includes(timeWindow)) {
      return false;
    }

    if (tabConfig?.isTotalSize === false) {
      return false;
    }

    if (searchParams?.isCompareOpen) {
      if (!searchParams?.timeWindowCompare) {
        return false;
      }

      if (![TimeType.TIME_TYPE_MINUTE5, TimeType.TIME_TYPE_HOUR].includes(searchParams.timeWindowCompare)) {
        return false;
      }
    }

    return true;
  };

  return <Card className={styles.card} {...props.cardProps}>
    <div className={styles.head}>
      {showTitle ? <span className={styles.headTitle}>
        {searchParams?.isCompareOpen ? t('趋势对比') : t('趋势分析')}
      </span> : null}
      <div>
        {showDownload && (chartData?.length || !isEmpty(groupData))
          ? <span className={styles.downloadIcon} onClick={exportData}><Tooltip
            title={t('导出趋势数据')}><DownloadLine /></Tooltip></span> : null}
        {showSwitch ? <span className={styles.switch} onClick={() => {
          setStorageVisible(visible => !visible);
          reportClick(
            pgId,
            !storageVisible ? CUSTOM_ACTIONS.commonExpandTrendAnalysis : CUSTOM_ACTIONS.commonCloseTrendAnalysis,
            {
              from: pgId,
            },
          );
        }}>
          <span>{storageVisible ? t('收起') : t('展开')}</span>
          <DownOutlined className={classnames({
            [styles.headIcon]: true,
            [styles.up]: storageVisible,
            [styles.down]: !storageVisible,
          })} />
        </span> : null}
      </div>

    </div>
    {storageVisible ? <div className={styles.center}>

      <div className={styles.filters}>
        {unitOptions?.length
          ? <Select onChange={unitChange} value={state.unit} className={styles.unit} options={unitOptions} /> : null}
        {unitOptionsForCombine?.length
          ? <Select onChange={unitChange} value={state.unit} className={styles.unit} options={unitOptionsForCombine} />
          : null}
        {showStatisticalDimension()
          ? <div className={styles.statisticalDimension}>
            <label className={styles.label}>{t('统计维度')}</label>
            <Radio.Group onChange={totalSizeChange} value={state.totalSize}>
              <Radio value={false}>{timeWindow === TimeType.TIME_TYPE_MINUTE5 ? t('5分钟') : t('1小时')}</Radio>
              <Radio value={true}>{t('累计')}</Radio>
            </Radio.Group>
          </div> : null}
      </div>
      <Tabs
        tabBarStyle={props.tabBarStyle}
        onChange={tabChange}
        activeKey={String(state.tabKey)}
        className={styles.tab}
      >
        {tabConfigs.map(item => <TabPane tab={item.name} key={item.key} />)}
      </Tabs>
    </div> : null}
    {state.tabKey === IssueMetricType.ISSUE_METRIC_LAG_REPORT_RATE
      && (isAndroid() || isIOS() || isAppleTV()) && lagRateTips}
    {storageVisible ? <Chart
      unitFormat={unitOptions?.length ? tabConfig?.unitConfig?.unitFormat?.(state.unit) : undefined}
      loading={loadingState}
      chartData={chartData}
      groupData={groupData}
      unitLabel={unitOptions?.length ? unitLabel : ''}
      timeWindow={timeWindow}
      tabKey={state.tabKey}
      compareChartData={compareChartData}
      compareTimeWindow={searchParams?.timeWindowCompare}
      metricStateGroup={tabConfig?.metricStateGroup}
      combineDisplayGroup={tabConfig?.combineDisplayGroup}
      tabName={String(tabConfig?.stringName || tabConfig?.name)}
      state={state}
    /> : null}

  </Card>;
});

